import { useEffect, useState } from 'react';
import { apiBytes, catalogMediaUrl } from '../../api';
import { bytesToBlob } from '../../crypto/crypto';
import type { MediaRow } from '../../data/dto';

export type MediaBlobState = 'idle' | 'loading' | 'ready' | 'locked' | 'error';

export interface MediaBlobResult {
  state: MediaBlobState;
  /** Object URL for the decrypted image (revoked on unmount / change). */
  url: string | null;
  /** The decrypted Blob — for download / Web Share. */
  blob: Blob | null;
}

// Bounded memory cache for decrypted blobs to avoid re-fetching and re-decryption.
// Capped by both maximum byte size (50MB) and max items (50) to prevent mobile browser OOM.
const blobCache = new Map<string, Blob>();
const cacheOrder: string[] = [];
let totalCacheBytes = 0;
const MAX_CACHE_BYTES = 50 * 1024 * 1024; // 50 MB max
const MAX_CACHE_ITEMS = 50;

function getCachedBlob(key: string): Blob | undefined {
  if (blobCache.has(key)) {
    const idx = cacheOrder.indexOf(key);
    if (idx > -1) cacheOrder.splice(idx, 1);
    cacheOrder.push(key);
    return blobCache.get(key);
  }
  return undefined;
}

function setCachedBlob(key: string, blob: Blob): void {
  if (blobCache.has(key)) {
    const old = blobCache.get(key);
    if (old) totalCacheBytes -= old.size;
    const idx = cacheOrder.indexOf(key);
    if (idx > -1) cacheOrder.splice(idx, 1);
  }

  while (cacheOrder.length > 0 && (cacheOrder.length >= MAX_CACHE_ITEMS || totalCacheBytes + blob.size > MAX_CACHE_BYTES)) {
    const oldestKey = cacheOrder.shift();
    if (oldestKey) {
      const removed = blobCache.get(oldestKey);
      if (removed) totalCacheBytes -= removed.size;
      blobCache.delete(oldestKey);
    }
  }

  blobCache.set(key, blob);
  totalCacheBytes += blob.size;
  cacheOrder.push(key);
}

/**
 * Fetch a media object's raw bytes (ciphertext for E2EE), decrypt client-side
 * when needed, and expose both an object URL (for <img>) and the Blob (for
 * download/share). The object URL is revoked automatically.
 *
 * `active` lets callers defer the fetch (e.g. a lightbox only loads the
 * currently-visible image).
 */
export function useMediaBlob(
  media: MediaRow,
  canDecrypt: boolean,
  decrypt: (bytes: Uint8Array) => Uint8Array,
  active = true,
  thumbnail = false,
): MediaBlobResult {
  const [state, setState] = useState<MediaBlobState>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (!active || !media?.id) {
      setState('idle');
      setUrl(null);
      setBlob(null);
      return;
    }
    let alive = true;
    let objUrl: string | null = null;

    const cacheKey = `${media.id}_${thumbnail ? 'thumb' : 'full'}`;
    const cached = getCachedBlob(cacheKey);
    if (cached) {
      objUrl = URL.createObjectURL(cached);
      setBlob(cached);
      setUrl(objUrl);
      setState('ready');
      return () => {
        if (objUrl) URL.revokeObjectURL(objUrl);
      };
    }

    (async () => {
      if (media.is_e2e_encrypted && !canDecrypt) {
        setState('locked');
        return;
      }
      setState('loading');
      try {
        // ?w=400 returns the thumbnail: a server-resized JPEG for plaintext, or
        // the client-uploaded encrypted thumbnail for E2EE media (the server
        // can't resize ciphertext). Falls back to the full blob when no thumb.
        const path = thumbnail
          ? `/media/${media.id}/raw?w=400`
          : `/media/${media.id}/raw`;
        const raw = await apiBytes(path);
        const plain = media.is_e2e_encrypted ? decrypt(raw) : raw;
        if (!alive) return;
        // A video's thumbnail is a JPEG poster frame, not a video/* blob —
        // tag it as an image so <img src={objectUrl}> can actually render it.
        const isVideoThumb = thumbnail && media.media_type === 'video';
        const b = bytesToBlob(plain, isVideoThumb ? 'image/jpeg' : media.mime_type);
        setCachedBlob(cacheKey, b);
        objUrl = URL.createObjectURL(b);
        setBlob(b);
        setUrl(objUrl);
        setState('ready');
      } catch {
        if (alive) setState('error');
      }
    })();

    return () => {
      alive = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [media.id, media.is_e2e_encrypted, media.mime_type, media.media_type, canDecrypt, decrypt, active, thumbnail]);

  return { state, url, blob };
}

/**
 * Like useMediaBlob, but short-circuits PUBLIC catalog images. When `isCatalog`
 * is true and the media isn't E2E-encrypted, it returns the unauthenticated
 * catalogMediaUrl (native <img> lazy-load + browser HTTP cache) and skips the
 * fetch→decrypt→objectURL path entirely. For everything else (parent photos,
 * encrypted media) it behaves exactly like useMediaBlob.
 *
 * The underlying hook is still called every render (with `active: false` in the
 * catalog case) so hook order stays stable.
 */
export function useCatalogOrBlob(
  media: MediaRow,
  isCatalog: boolean,
  canDecrypt: boolean,
  decrypt: (bytes: Uint8Array) => Uint8Array,
  active = true,
  thumbnail = false,
): MediaBlobResult {
  // The public /catalog/media endpoint only serves images — catalog videos and
  // audio must still stream through the authed /media/{id}/raw path.
  const isImage = (media?.mime_type ?? '').startsWith('image/');
  const useCatalog = isCatalog && !!media?.id && !media.is_e2e_encrypted && isImage;
  const blob = useMediaBlob(media, canDecrypt, decrypt, active && !useCatalog, thumbnail);
  if (useCatalog) {
    return {
      state: 'ready',
      url: catalogMediaUrl(media.id, { thumb: thumbnail }),
      blob: null,
    };
  }
  return blob;
}

/** A friendly download filename for a media row. */
export function mediaFilename(media: MediaRow): string {
  const ext = media.mime_type.includes('png') ? 'png' : media.mime_type.includes('webp') ? 'webp' : 'jpg';
  return `babysteps-${media.id.slice(0, 8)}.${ext}`;
}
